/*eslint no-prototype-builtins: "off"*/
import React from 'react'
import Checkbox from '@src/components/common/checkbox'
import { TableProps, CellInfo } from 'react-table-6'
import { ExtColumn, ColumnProps } from '../models'
import { some } from 'lodash'
import { isDisabled } from '../helpers/columns.manager'

interface SelectInputProps<T = {}> {
  checked: boolean
  disabled?: boolean
  id?: T[keyof T]
  rowData?: T
  onClick: (id: T[keyof T], shiftKey: boolean, row: T) => void
}

function DefaultSelectInputComponent<T>({
  rowData,
  id,
  disabled,
  onClick,
  checked
}: SelectInputProps<T>): React.ReactElement<SelectInputProps<T>> {
  return (
    <Checkbox
      name="checkbox"
      disabled={disabled}
      value={checked}
      label=""
      onChange={e => {
        const { shiftKey } = e
        e.stopPropagation()
        onClick(id, shiftKey, rowData)
      }}
    />
  )
}

export type ExpandedRows = { [key: number]: boolean }

export interface RTSelectTableProps<T> extends TableProps<T> {
  id?: string
  role?: string
  keyField?: keyof T
  toggleSelection?: (id, shiftKey: boolean, row: T) => void
  isSelected?: (data: T[keyof T]) => boolean
  SelectInputComponent?: React.FC<SelectInputProps<T>>
  toggleAll?: (id: T[keyof T], shiftKey: boolean, row: T) => void
  selectAll?: boolean
  SelectAllInputComponent?: React.FC<SelectInputProps<T>>
  selectWidth?: number
  columns?: ExtColumn<T>[]
  filterAll?: boolean
  renderCurrentPage?: (page?: number) => void
  renderTotalPagesCount?: (page?: number) => void
  expanded: ExpandedRows
  noSelectAll?: boolean
}

export default Component => {
  const wrapper = class RTSelectTable<T> extends React.PureComponent<
    RTSelectTableProps<T>
  > {
    static displayName = 'RTSelectTable'
    static defaultProps = {
      keyField: null,
      selectAll: false,
      SelectInputComponent: DefaultSelectInputComponent,
      SelectAllInputComponent: DefaultSelectInputComponent,
      noSelectAll: false
    }
    wrappedInstance = null

    rowSelector = (rowData: T, column: ExtColumn<T>) => {
      if (!rowData || !rowData.hasOwnProperty(this.props.keyField)) return null
      const { toggleSelection, keyField } = this.props
      const checked = this.props.isSelected(rowData[this.props.keyField])
      const inputProps: SelectInputProps<T> = {
        checked,
        onClick: toggleSelection,
        id: rowData[keyField],
        disabled: isDisabled(column, disable => disable(rowData)),
        rowData
      }
      return React.createElement(this.props.SelectInputComponent, inputProps)
    }

    isDisabledHeaderColumn = <T extends {}>(col: ColumnProps<T>, items: T[]) =>
      isDisabled(col, disable => !some(items, x => !disable(x)))

    headSelector = (
      cellInfo: CellInfo & { data?: T[] },
      column: ExtColumn<T>
    ) => {
      const {
        toggleAll,
        selectAll: checked,
        SelectAllInputComponent,
        noSelectAll
      } = this.props

      if (noSelectAll) return null

      const inputProps: SelectInputProps<T> = {
        checked,
        disabled: this.isDisabledHeaderColumn(column, cellInfo?.data || []),
        onClick: toggleAll
      }

      return React.createElement(SelectAllInputComponent, inputProps)
    }

    getWrappedInstance() {
      if (this.wrappedInstance.getWrappedInstance) {
        return this.wrappedInstance.getWrappedInstance()
      }
      return this.wrappedInstance
    }

    render() {
      const {
        columns: originalCols,
        isSelected,
        toggleSelection,
        toggleAll,
        keyField,
        selectAll,
        selectWidth,
        SelectAllInputComponent,
        SelectInputComponent,
        ...rest
      } = this.props

      const selector = originalCols.findIndex(x => x.isSelector === true)
      const columnProps = originalCols.find(x => x.isSelector === true)

      const select: ExtColumn<T> = {
        id: '_selector',
        accessor: x => 'x',
        Header: ci => this.headSelector(ci, columnProps),
        Cell: ci => this.rowSelector(ci.original, columnProps),
        width: selectWidth || 30,
        filterable: false,
        sortable: false,
        resizable: false,
        style: { textAlign: 'center' },
        //headerClassName:
        headerStyle: { textAlign: 'center', paddingTop: '15px' }
      }

      if (selector !== -1 && this.props.keyField) {
        originalCols.splice(selector, 1, { ...columnProps, ...select })
      }
      const columns = originalCols
      const extra = {
        columns
      }
      return (
        <Component.default
          {...rest}
          {...extra}
          ref={r => (this.wrappedInstance = r)}
        />
      )
    }
  }

  return wrapper
}
